import type { ApiClientConfig, EnvironmentConfig } from '../../types/api/common';

// Environment configuration
const getEnvironmentConfig = (): EnvironmentConfig => {
  const env = import.meta.env.MODE || 'development';
  
  const config: Record<string, EnvironmentConfig> = {
    development: {
      // Same-origin `/api` is proxied by Vite to the real API (see vite.config.ts) — avoids CORS on localhost.
      API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '/api',
      API_TIMEOUT: 30000,
      ENABLE_CACHE: true,
      ENABLE_RETRY: true,
      LOG_LEVEL: 'debug',
    },
    staging: {
      API_BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://api.saung.axra.com.mm',
      API_TIMEOUT: 25000,
      ENABLE_CACHE: true,
      ENABLE_RETRY: true,
      LOG_LEVEL: 'info',
    },
    production: {
      API_BASE_URL: import.meta.env.VITE_API_BASE_URL || 'https://api.saung.axra.com.mm',
      API_TIMEOUT: 20000,
      ENABLE_CACHE: true,
      ENABLE_RETRY: true,
      LOG_LEVEL: 'error',
    },
  };

  return config[env] || config.development;
};

// Default API client configuration
export const createApiConfig = (): ApiClientConfig => {
  const envConfig = getEnvironmentConfig();
  
  return {
    baseURL: envConfig.API_BASE_URL,
    timeout: envConfig.API_TIMEOUT,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-Version': '1.0.0',
      'X-Platform': 'web',
    },
    cache: {
      enabled: envConfig.ENABLE_CACHE,
      ttl: 5 * 60 * 1000, // 5 minutes
      maxSize: 100, // Maximum number of cached requests
      strategy: 'memory',
    },
    retry: {
      attempts: envConfig.ENABLE_RETRY ? 3 : 0,
      delay: 1000, // Base delay of 1 second
      backoffFactor: 2, // Exponential backoff
      retryCondition: (error: any) => {
        // Handle AxiosError (raw error from axios)
        if (error.isAxiosError || error.response || error.request) {
          // Network error (no response received)
          if (error.request && !error.response) {
            return true;
          }
          
          // Server error (5xx status codes)
          if (error.response?.status >= 500 && error.response?.status < 600) {
            return true;
          }
          
          // Timeout error
          if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            return true;
          }
          
          return false;
        }
        
        // Handle ApiErrorResponse (processed error)
        if (error.error) {
          const isNetworkError = error.error.type === 'NETWORK_ERROR';
          const isServerError = error.error.code?.startsWith('5') || error.error.code?.startsWith('HTTP_5');
          const isTimeoutError = error.error.code === 'TIMEOUT';
          
          return isNetworkError || isServerError || isTimeoutError;
        }
        
        return false;
      },
    },
    interceptors: {
      request: [],
      response: [],
    },
  };
};

// API endpoints configuration
export const API_ENDPOINTS = {
  // Authentication endpoints
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH_TOKEN: '/auth/refresh',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    CHANGE_PASSWORD: '/auth/change-password',
    VERIFY_EMAIL: '/auth/verify-email',
    RESEND_VERIFICATION: '/auth/resend-verification',
    VERIFY_PHONE: '/auth/verify-phone',
    VERIFY_OTP: '/auth/verify-email',
    PROFILE: '/auth/profile',
  },

  // Admin authentication endpoints
  ADMIN: {
    LOGIN: '/admin/auth/login',
    REGISTER: '/admin/auth/register',
    LOGOUT: '/admin/auth/logout',
    RESEND_VERIFICATION: '/admin/auth/resend-verification',
    VERIFY_EMAIL: '/admin/auth/verify-email',
  },

  // User management endpoints (Admin)
  USERS: {
    LIST: '/users/admin/list',
    CREATE: '/users/admin/create',
    DETAILS: (id: string) => `/users/${id}`,
    UPDATE: (id: string) => `/users/${id}`,
    DELETE: (id: string) => `/users/${id}`,
    TOGGLE_STATUS: (id: string) => `/users/${id}/status`,
    UPDATE_ROLE: (id: string) => `/users/${id}/role`,
    VERIFY_EMAIL: (id: string) => `/users/${id}/verify-email`,
    VERIFY_PHONE: (id: string) => `/users/${id}/verify-phone`,
    SUSPEND: (id: string) => `/users/${id}/suspend`,
    UNSUSPEND: (id: string) => `/users/${id}/unsuspend`,
    STATS: '/users/admin/stats',
    EXPORT: '/users/admin/export',
    BULK_UPDATE_STATUS: '/users/admin/bulk-update-status',
    BULK_DELETE: '/users/admin/bulk-delete',
    // Admin user management endpoints
    ADMIN_LIST: '/admin/users',
    ADMIN_DETAIL: (id: string) => `/admin/users/${id}`,
    ADMIN_UPDATE: (id: string) => `/admin/users/${id}`,
    ADMIN_SOFT_DELETE: (id: string) => `/admin/users/${id}`,
    ADMIN_RESTORE: (id: string) => `/admin/users/${id}/restore`,
    ADMIN_DELETED_LIST: '/admin/users/deleted',
    ADMIN_DELETED_STATUS: (id: string) => `/admin/users/${id}/deleted-status`,
  },

  // Merchant authentication endpoints
  MERCHANT: {
    LOGIN: '/merchant/login',
    REGISTER: '/merchant/register',
    LOGOUT: '/merchant/logout',
    RESEND_VERIFICATION: '/merchant/resend-verification',
    VERIFY_EMAIL: '/merchant/verify-email',
  },
  
  // Restaurant endpoints
  RESTAURANTS: {
    LIST: '/restaurants',
    BROWSE: '/restaurants/browse',
    MERCHANT_RESTAURANTS: (merchantId: string) => `/restaurants/merchant/${merchantId}`, // Merchant ID in URL path for fetching
    DETAILS: (id: string) => `/restaurants/${id}`,
    UPDATE: (id: string) => `/restaurants/${id}`,
    MENU: (id: string) => `/restaurants/${id}/menu`,
    MENU_ITEM: (restaurantId: string, itemId: string) => `/restaurants/${restaurantId}/menu/${itemId}`,
    REVIEWS: (id: string) => `/restaurants/${id}/reviews`,
    SEARCH: '/restaurants/search',
  },

  // Product endpoints
  PRODUCTS: {
    LIST: '/products', // GET with query params (page, limit, merchantId for merchant-specific)
    CREATE: '/products', // POST
    DETAILS: (id: string) => `/products/${id}`, // GET
    UPDATE: (id: string) => `/products/${id}`, // PUT/PATCH
    DELETE: (id: string) => `/products/${id}`, // DELETE
    SEARCH: '/products/search', // GET with query params
    BY_MERCHANT: '/products', // GET with merchantId query param
    BY_RESTAURANT: (restaurantId: string) => `/products?restaurantId=${restaurantId}`, // GET
  },

  // Category endpoints
  CATEGORIES: {
    LIST: '/categories', // GET with query params (page, limit)
    CREATE: '/categories', // POST
    DETAILS: (id: string) => `/categories/${id}`, // GET
    UPDATE: (id: string) => `/categories/${id}`, // PUT/PATCH
    DELETE: (id: string) => `/categories/${id}`, // DELETE
    SEARCH: '/categories/search', // GET with query params
  },
  
  // Order endpoints
  ORDERS: {
    LIST: '/orders',
    CREATE: '/orders',
    DETAILS: (id: string) => `/orders/${id}`,
    UPDATE_STATUS: (id: string) => `/orders/${id}/status`,
    CANCEL: (id: string) => `/orders/${id}/cancel`,
    TRACK: (id: string) => `/orders/${id}/track`,
    STATS: '/orders/stats',
  },
  
  // Cart endpoints
  CART: {
    GET: '/cart',
    ADD_ITEM: '/cart/items',
    UPDATE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    REMOVE_ITEM: (itemId: string) => `/cart/items/${itemId}`,
    CLEAR: '/cart/clear',
  },
  
  // Payment endpoints
  PAYMENTS: {
    METHODS: '/payments/methods',
    ADD_METHOD: '/payments/methods',
    REMOVE_METHOD: (id: string) => `/payments/methods/${id}`,
    CREATE_INTENT: '/payments/intents',
    CONFIRM: (intentId: string) => `/payments/intents/${intentId}/confirm`,
    TRANSACTIONS: '/payments/transactions',
    REFUND: '/payments/refunds',
    WALLET: '/payments/wallet',
    WALLET_ADD_FUNDS: '/payments/wallet/add-funds',
    STATS: '/payments/stats',
  },

  // Payment Method Management endpoints (Admin & Public)
  PAYMENT_METHODS: {
    // Admin APIs
    ADMIN_LIST: '/admin/payment-methods', // GET paginated list (includes inactive)
    ADMIN_DETAIL: (paymentMethodId: string) => `/admin/payment-methods/${paymentMethodId}`, // GET by ID
    ADMIN_CREATE_MULTIPART: '/admin/payment-methods', // POST multipart with `data` + optional `logo` + optional `qrImage`
    ADMIN_CREATE_JSON: '/admin/payment-methods/json', // POST JSON
    ADMIN_UPDATE_MULTIPART: (paymentMethodId: string) => `/admin/payment-methods/${paymentMethodId}`, // PUT multipart
    ADMIN_UPDATE_JSON: (paymentMethodId: string) => `/admin/payment-methods/${paymentMethodId}/json`, // PUT JSON
    ADMIN_ACTIVATE: (paymentMethodId: string) => `/admin/payment-methods/${paymentMethodId}/activate`, // PUT activate
    ADMIN_DEACTIVATE: (paymentMethodId: string) => `/admin/payment-methods/${paymentMethodId}/deactivate`, // PUT deactivate
    ADMIN_DELETE: (paymentMethodId: string) => `/admin/payment-methods/${paymentMethodId}`, // DELETE

    // Public APIs
    PUBLIC_ACTIVE_SIMPLE: '/public/payment-methods/active', // GET list active (simple)
    PUBLIC_ACTIVE_PAGINATED: '/public/payment-methods', // GET paginated active list
    PUBLIC_DETAIL: (paymentMethodId: string) => `/public/payment-methods/${paymentMethodId}`, // GET by ID
  },
  
  // Dashboard endpoints (legacy — kept for backward compatibility with non-analytics widgets)
  DASHBOARD: {
    OVERVIEW: '/dashboard/overview',
    ANALYTICS: '/dashboard/analytics',
    REPORTS: '/dashboard/reports',
  },

  // Admin Analytics endpoints (Phase 1)
  // Each endpoint accepts ?from=YYYY-MM-DD&to=YYYY-MM-DD as a date range filter.
  ANALYTICS: {
    USERS_OVERVIEW: '/admin/analytics/users/overview',
    SUBSCRIPTIONS_OVERVIEW: '/admin/analytics/subscriptions/overview',
    SUBSCRIPTIONS_REVENUE: '/admin/analytics/subscriptions/revenue',
    SUBSCRIPTIONS_PLANS: '/admin/analytics/subscriptions/plans',
  },
  
  // Admin Language endpoints
  ADMIN_LANGUAGES: {
    LIST: '/admin/languages',
    CREATE: '/admin/languages',
    DETAILS: (id: string) => `/admin/languages/${id}`,
    UPDATE: (id: string) => `/admin/languages/${id}`,
    DELETE: (id: string) => `/admin/languages/${id}`,
  },

  // Genres endpoints
  GENRES: {
    PUBLIC_LIST: '/public/genres', // GET with page,size
    ADMIN_LIST: '/admin/genres',   // GET with page,size (if needed)
    CREATE: '/admin/genres',       // POST
    DETAILS: (id: string) => `/admin/genres/${id}`,
    UPDATE: (id: string) => `/admin/genres/${id}`,
    DELETE: (id: string) => `/admin/genres/${id}`,
  },

  // Movies endpoints
  MOVIES: {
    PUBLIC_LIST: '/public/movies', // GET with page,size
    PUBLIC_DETAILS: (id: string) => `/public/movies/${id}`, // GET movie details with genres
    ADMIN_LIST: '/admin/movies',   // GET with page,size (if needed)
    CREATE: '/admin/movies',       // POST with form-data
    DETAILS: (id: string) => `/admin/movies/${id}`,
    UPDATE: (id: string) => `/admin/movies/${id}`, // PUT JSON partial update
    DELETE: (id: string) => `/admin/movies/${id}`,
    // Thumbnail-only update (multipart/form-data, thumbnail part)
    ADMIN_UPDATE_THUMBNAIL: (id: string) => `/admin/movies/${id}/thumbnail`,
    // Cast assignment for movies
    ADMIN_ASSIGN_CAST: (movieId: string) => `/admin/movies/${movieId}/casts`,
    ADMIN_REMOVE_CAST: (movieId: string, castId: string) =>
      `/admin/movies/${movieId}/casts/${castId}`,
    ADMIN_UPDATE_CAST_ROLE: (movieId: string, castId: string) =>
      `/admin/movies/${movieId}/casts/${castId}/role`,
    PUBLIC_LIST_CASTS: (movieId: string) => `/public/movies/${movieId}/casts`,
  },

  // Dramas / Series endpoints (shared Drama aggregate)
  DRAMAS: {
    PUBLIC_LIST: '/public/dramas', // GET with page,size,keyword,dramaType
    PUBLIC_DETAILS: (id: string) => `/public/dramas/${id}`, // GET drama/series details
    ADMIN_LIST: '/admin/dramas', // GET with page,size,keyword,dramaType (admin dashboard)
    ADMIN_CREATE_MULTIPART: '/admin/dramas', // POST multipart with `data` + `thumbnail`
    ADMIN_CREATE_JSON: '/admin/dramas/json', // POST JSON body with optional thumbnailUrl
    ADMIN_UPDATE: (id: string) => `/admin/dramas/${id}`, // PUT JSON partial update
    ADMIN_SOFT_DELETE: (id: string) => `/admin/dramas/${id}`, // DELETE soft delete
    ADMIN_PERMANENT_DELETE: (id: string) => `/admin/dramas/${id}/permanent`, // DELETE hard delete
    ADMIN_ASSIGN_CAST: (id: string) => `/admin/dramas/${id}/casts`, // POST assign cast
    ADMIN_REMOVE_CAST: (id: string, castId: string) => `/admin/dramas/${id}/casts/${castId}`, // DELETE remove cast
    ADMIN_UPDATE_CAST_ROLE: (id: string, castId: string) =>
      `/admin/dramas/${id}/casts/${castId}/role`, // PUT update cast role with ?role=
    PUBLIC_LIST_CASTS: (id: string) => `/public/dramas/${id}/casts`, // GET casts list

    // Episode endpoints under a drama/series
    PUBLIC_EPISODES: (dramaId: string) => `/public/dramas/${dramaId}/episodes`,
    PUBLIC_EPISODE_DETAILS: (dramaId: string, episodeId: string) =>
      `/public/dramas/${dramaId}/episodes/${episodeId}`,
    ADMIN_CREATE_EPISODE_MULTIPART: (dramaId: string) => `/admin/dramas/${dramaId}/episodes`,
    ADMIN_CREATE_EPISODE_JSON: (dramaId: string) => `/admin/dramas/${dramaId}/episodes/json`,
    ADMIN_UPDATE_EPISODE: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}`,
    ADMIN_SOFT_DELETE_EPISODE: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}`,
    ADMIN_PERMANENT_DELETE_EPISODE: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/permanent`,
    PUBLIC_SEASONS: (dramaId: string) => `/public/dramas/${dramaId}/seasons`,
    PUBLIC_EPISODE_BY_SEASON_NUMBER: (
      dramaId: string,
      season: number,
      episodeNumber: number
    ) => `/public/dramas/${dramaId}/seasons/${season}/episodes/${episodeNumber}`,

    // Episode-level video upload & management endpoints
    ADMIN_EPISODE_VIDEOS_INITIATE_MULTIPART: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/videos/initiate-multipart`,
    ADMIN_EPISODE_VIDEOS_COMPLETE_MULTIPART: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/videos/complete-multipart`,
    ADMIN_EPISODE_VIDEOS_MULTIPART_STATUS: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/videos/multipart-status`,
    ADMIN_EPISODE_VIDEOS_REGENERATE_PART_URLS: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/videos/regenerate-part-urls`,
    ADMIN_EPISODE_VIDEOS_ABORT: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/videos/abort`,
    ADMIN_EPISODE_VIDEOS_UPLOAD_DIRECT: (dramaId: string, episodeId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/videos/upload`,
    ADMIN_EPISODE_VIDEOS_DELETE: (dramaId: string, episodeId: string, fileId: string) =>
      `/admin/dramas/${dramaId}/episodes/${episodeId}/videos/${fileId}`,
  },

  // Music Videos endpoints
  MUSIC_VIDEOS: {
    PUBLIC_LIST: '/public/music-videos',
    PUBLIC_DETAILS: (id: string) => `/public/music-videos/${id}`,
    ADMIN_LIST: '/admin/music-videos',
    ADMIN_DETAIL: (id: string) => `/admin/music-videos/${id}`,
    ADMIN_CREATE: '/admin/music-videos',
    ADMIN_UPDATE: (id: string) => `/admin/music-videos/${id}`,
    ADMIN_DELETE: (id: string) => `/admin/music-videos/${id}`,
  },

  // SAUNG music library endpoints — Tracks (MP3). Backend: /api/admin/tracks
  // Multipart CRUD: `data` (JSON) + optional `audio` file.
  SONGS: {
    ADMIN_LIST: '/admin/tracks',
    ADMIN_DETAIL: (id: string) => `/admin/tracks/${id}`,
    ADMIN_CREATE: '/admin/tracks',
    ADMIN_UPDATE: (id: string) => `/admin/tracks/${id}`,
    ADMIN_DELETE: (id: string) => `/admin/tracks/${id}`,
  },
  ARTISTS: {
    ADMIN_LIST: '/admin/artists',
    ADMIN_DETAIL: (id: string) => `/admin/artists/${id}`,
    ADMIN_CREATE: '/admin/artists',
    ADMIN_UPDATE: (id: string) => `/admin/artists/${id}`,
    ADMIN_DELETE: (id: string) => `/admin/artists/${id}`,
  },
  PLAYLISTS: {
    ADMIN_LIST: '/admin/playlists',
    ADMIN_DETAIL: (id: string) => `/admin/playlists/${id}`,
    ADMIN_CREATE: '/admin/playlists',
    ADMIN_UPDATE: (id: string) => `/admin/playlists/${id}`,
    ADMIN_DELETE: (id: string) => `/admin/playlists/${id}`,
  },

  // Albums endpoints
  ALBUMS: {
    ADMIN_LIST: '/admin/albums', // GET with page,size,keyword (admin dashboard)
    ADMIN_CREATE_MULTIPART: '/admin/albums', // POST multipart with `data` + optional `coverImage` and `image`
    ADMIN_DETAIL: (albumId: string) => `/admin/albums/${albumId}`, // GET by ID
    ADMIN_UPDATE_MULTIPART: (albumId: string) => `/admin/albums/${albumId}`, // PUT multipart with `data` + optional files
    ADMIN_DELETE: (albumId: string) => `/admin/albums/${albumId}`, // DELETE
  },

  // Documentaries endpoints
  DOCUMENTARIES: {
    PUBLIC_LIST: '/public/documentaries', // GET with page,size,keyword,languageId,genreId,documentaryType
    PUBLIC_DETAILS: (id: string) => `/public/documentaries/${id}`, // GET documentary details
    ADMIN_LIST: '/admin/documentaries', // GET with page,size,keyword (admin dashboard)
    ADMIN_CREATE_MULTIPART: '/admin/documentaries', // POST multipart with `data` + `thumbnail`
    ADMIN_CREATE_JSON: '/admin/documentaries/json', // POST JSON body (no file)
    ADMIN_UPDATE: (id: string) => `/admin/documentaries/${id}`, // PUT JSON partial update
    ADMIN_SOFT_DELETE: (id: string) => `/admin/documentaries/${id}`, // DELETE soft delete
    ADMIN_PERMANENT_DELETE: (id: string) => `/admin/documentaries/${id}/permanent`, // DELETE hard delete
    // Thumbnail-only update (multipart/form-data, thumbnail part)
    ADMIN_UPDATE_THUMBNAIL: (id: string) => `/admin/documentaries/${id}/thumbnail`,
    // Video upload (content-level, same pattern as movies)
    ADMIN_INITIATE_MULTIPART_UPLOAD: (id: string) =>
      `/admin/content/${id}/videos/initiate-multipart`,
    ADMIN_COMPLETE_MULTIPART_UPLOAD: (id: string) =>
      `/admin/content/${id}/videos/complete-multipart`,
    ADMIN_GET_MULTIPART_STATUS: (id: string) =>
      `/admin/content/${id}/videos/multipart-status`,
    ADMIN_REGENERATE_PART_URLS: (id: string) =>
      `/admin/content/${id}/videos/regenerate-part-urls`,
    ADMIN_ABORT_MULTIPART_UPLOAD: '/admin/content/videos/abort',
    ADMIN_DELETE_VIDEO: (id: string, fileId: string) => `/admin/content/${id}/videos/${fileId}`,
  },

  // App Update (APK) endpoints
  APP_UPDATES: {
    ADMIN_LIST: '/admin/app-updates',
    ADMIN_CREATE: '/admin/app-updates',
    ADMIN_DETAILS: (id: string) => `/admin/app-updates/${id}`,
    ADMIN_UPDATE: (id: string) => `/admin/app-updates/${id}`,
    ADMIN_DELETE: (id: string) => `/admin/app-updates/${id}`,
  },

  // Casts endpoints (admin & public)
  CASTS: {
    // Admin CRUD
    ADMIN_LIST: '/admin/casts', // GET paginated list (admin dashboard)
    ADMIN_CREATE_MULTIPART: '/admin/casts', // POST multipart with `data` + `profileImage`
    ADMIN_CREATE_JSON: '/admin/casts/json', // POST JSON body
    ADMIN_UPDATE_MULTIPART: (castId: string) => `/admin/casts/${castId}`, // PUT multipart
    ADMIN_UPDATE_JSON: (castId: string) => `/admin/casts/${castId}/json`, // PUT JSON
    ADMIN_DELETE: (castId: string) => `/admin/casts/${castId}`, // DELETE

    // Public read APIs
    PUBLIC_GET: (castId: string) => `/public/casts/${castId}`, // GET single cast
    PUBLIC_LIST: '/public/casts', // GET paginated casts
    PUBLIC_SEARCH: '/public/casts/search', // GET search with keyword,page,size
    PUBLIC_BY_NATIONALITY: (nationality: string) =>
      `/public/casts/nationality/${encodeURIComponent(nationality)}`, // GET list by nationality
    PUBLIC_POPULAR: '/public/casts/popular', // GET list ordered by content count
  },

  // Subscription Management endpoints
  SUBSCRIPTIONS: {
    CREATE: '/admin/subscriptions/create', // POST create subscription for user
  },

  // Subscription Plan Management endpoints
  SUBSCRIPTION_PLANS: {
    LIST: '/admin/subscription-plans', // GET list all plans with filters
    CREATE: '/admin/subscription-plans', // POST create new plan
    DETAIL: (id: string) => `/admin/subscription-plans/${id}`, // GET plan by ID
    UPDATE: (id: string) => `/admin/subscription-plans/${id}`, // PUT update plan
    ACTIVATE: (id: string) => `/admin/subscription-plans/${id}/activate`, // PUT activate plan
    DEACTIVATE: (id: string) => `/admin/subscription-plans/${id}/deactivate`, // PUT deactivate plan
    DELETE: (id: string) => `/admin/subscription-plans/${id}`, // DELETE plan
  },

  // Banner Management endpoints
  BANNERS: {
    ADMIN_LIST: '/admin/banners', // GET list with pagination & filters
    ADMIN_DETAIL: (id: string) => `/admin/banners/${id}`, // GET by ID
    ADMIN_CREATE_MULTIPART: '/admin/banners', // POST multipart with `data` + `image`
    ADMIN_CREATE_JSON: '/admin/banners/json', // POST JSON with imageUrl
    ADMIN_UPDATE_MULTIPART: (id: string) => `/admin/banners/${id}`, // PUT multipart
    ADMIN_UPDATE_JSON: (id: string) => `/admin/banners/${id}/json`, // PUT JSON
    ADMIN_DELETE: (id: string) => `/admin/banners/${id}`, // DELETE
    ADMIN_ACTIVATE: (id: string) => `/admin/banners/${id}/activate`, // PUT activate
    ADMIN_DEACTIVATE: (id: string) => `/admin/banners/${id}/deactivate`, // PUT deactivate
    PUBLIC_LIST: '/public/banners', // GET active banners
    PUBLIC_FEATURED: '/public/banners/featured', // GET featured banners
  },

  // Trailer Management endpoints
  TRAILERS: {
    ADMIN_LIST: '/admin/trailers', // GET list with pagination & filters
    ADMIN_DETAIL: (id: string) => `/admin/trailers/${id}`, // GET by ID
    ADMIN_CREATE_MULTIPART: '/admin/trailers', // POST multipart with `data` + `video` + `thumbnail`
    ADMIN_CREATE_JSON: '/admin/trailers/json', // POST JSON with URLs
    ADMIN_UPDATE_MULTIPART: (id: string) => `/admin/trailers/${id}`, // PUT multipart
    ADMIN_UPDATE_JSON: (id: string) => `/admin/trailers/${id}/json`, // PUT JSON
    ADMIN_DELETE: (id: string) => `/admin/trailers/${id}`, // DELETE
    ADMIN_ACTIVATE: (id: string) => `/admin/trailers/${id}/activate`, // PUT activate
    ADMIN_DEACTIVATE: (id: string) => `/admin/trailers/${id}/deactivate`, // PUT deactivate
    PUBLIC_LIST: '/public/trailers', // GET active trailers
    PUBLIC_FEATURED: '/public/trailers/featured', // GET featured trailers
  },

  // Splash Screen Management endpoints
  SPLASH_SCREENS: {
    ADMIN_LIST: '/admin/splash-screens', // GET list with pagination & filters
    ADMIN_DETAIL: (id: string) => `/admin/splash-screens/${id}`, // GET by ID
    ADMIN_CREATE_MULTIPART: '/admin/splash-screens', // POST multipart with `data` + `image`
    ADMIN_CREATE_JSON: '/admin/splash-screens/json', // POST JSON with imageUrl
    ADMIN_UPDATE_MULTIPART: (id: string) => `/admin/splash-screens/${id}`, // PUT multipart
    ADMIN_UPDATE_JSON: (id: string) => `/admin/splash-screens/${id}/json`, // PUT JSON
    ADMIN_DELETE: (id: string) => `/admin/splash-screens/${id}`, // DELETE
    PUBLIC_LIST: '/public/splash-screens', // GET active splash screens
  },

  // Admin Content / International Content endpoints
  ADMIN_CONTENT: {
    INTERNATIONAL_ACTIVATE: '/admin/content/international/activate',
    INTERNATIONAL_DEACTIVATE: '/admin/content/international/deactivate',
  },

  // Utility endpoints
  UTILS: {
    HEALTH: '/health',
    VERSION: '/version',
    UPLOAD: '/upload',
  },
} as const;

// Request timeout configurations for different endpoint types
export const TIMEOUT_CONFIG = {
  DEFAULT: 30000,
  UPLOAD: 120000, // 2 minutes for file uploads
  PAYMENT: 60000, // 1 minute for payment operations
  SEARCH: 15000, // 15 seconds for search operations
} as const;

// Cache TTL configurations for different endpoint types
export const CACHE_TTL_CONFIG = {
  STATIC: 60 * 60 * 1000, // 1 hour for static data
  DYNAMIC: 5 * 60 * 1000, // 5 minutes for dynamic data
  USER_SPECIFIC: 2 * 60 * 1000, // 2 minutes for user-specific data
  REAL_TIME: 0, // No cache for real-time data
} as const;

// Rate limiting configurations
export const RATE_LIMIT_CONFIG = {
  DEFAULT: {
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 100,
  },
  AUTH: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    maxRequests: 5, // 5 login attempts per 15 minutes
  },
  SEARCH: {
    windowMs: 60 * 1000, // 1 minute
    maxRequests: 30,
  },
} as const;

// Security configurations
export const SECURITY_CONFIG = {
  ENABLE_REQUEST_SIGNING: import.meta.env.PROD,
  ENABLE_RESPONSE_VALIDATION: true,
  ENABLE_XSS_PROTECTION: true,
  ENABLE_CSRF_PROTECTION: true,
  ALLOWED_ORIGINS: [
    'http://localhost:3000',
    'http://localhost:5173',
    'https://cityexpress.com',
    'https://*.cityexpress.com',
  ],
} as const;
